(function () {
  const STORAGE_KEY = 'zety-wishlist-v1';
  const EVENT = 'zety:wishlist-updated';

  function read() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch {
      return [];
    }
  }

  function write(list) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { items: list } }));
  }

  function normalize(item) {
    if (!item || !item.handle) return null;
    return {
      handle: String(item.handle),
      id: item.id ? String(item.id) : '',
      title: item.title ? String(item.title) : '',
      image: item.image ? String(item.image) : '',
      url: item.url ? String(item.url) : `/products/${item.handle}`,
      price: item.price ? String(item.price) : '',
    };
  }

  const ZetyWishlist = {
    getItems() {
      return read();
    },

    getCount() {
      return read().length;
    },

    has(handle) {
      return read().some((item) => item.handle === handle);
    },

    toggle(item) {
      const data = normalize(item);
      if (!data) return false;
      let list = read();
      const exists = list.some((i) => i.handle === data.handle);
      if (exists) {
        list = list.filter((i) => i.handle !== data.handle);
        write(list);
        return false;
      }
      list.push(data);
      write(list);
      return true;
    },

    remove(handle) {
      const list = read().filter((i) => i.handle !== handle);
      write(list);
    },
  };

  window.ZetyWishlist = ZetyWishlist;

  function updateBadges(animate) {
    const count = ZetyWishlist.getCount();
    document.querySelectorAll('[data-z-wishlist-count]').forEach((el) => {
      const prev = Number.parseInt(el.textContent, 10) || 0;
      el.textContent = count > 99 ? '99+' : String(count);
      el.hidden = count === 0;
      if (animate && count !== prev) {
        el.classList.remove('z-head__badge--pulse');
        void el.offsetWidth;
        el.classList.add('z-head__badge--pulse');
      }
    });
    document.querySelectorAll('[data-z-wishlist-link]').forEach((link) => {
      link.classList.toggle('z-head__icon--wishlist-has-items', count > 0);
    });
  }

  function syncToggleButtons() {
    const items = new Set(ZetyWishlist.getItems().map((i) => i.handle));
    document.querySelectorAll('[data-z-wishlist-toggle]').forEach((btn) => {
      const handle = btn.getAttribute('data-product-handle');
      if (!handle) return;
      const active = items.has(handle);
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      btn.setAttribute(
        'aria-label',
        active ? 'Remove from wishlist' : 'Add to wishlist'
      );
    });
  }

  function bindToggles() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-z-wishlist-toggle]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      const handle = btn.getAttribute('data-product-handle');
      if (!handle) return;
      ZetyWishlist.toggle({
        handle,
        id: btn.getAttribute('data-product-id'),
        title: btn.getAttribute('data-product-title'),
        image: btn.getAttribute('data-product-image'),
        url: btn.getAttribute('data-product-url'),
        price: btn.getAttribute('data-product-price'),
      });
      updateBadges(true);
      syncToggleButtons();
    });
  }

  function formatMoney(cents, currency) {
    const amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD',
      }).format(amount);
    } catch {
      return `$${amount.toFixed(2)}`;
    }
  }

  class ZetyWishlistPage extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-z-wishlist-grid]');
      this.empty = this.querySelector('[data-z-wishlist-empty]');
      if (!this.grid) return;
      this._onUpdate = () => this.render();
      this.render();
      document.addEventListener(EVENT, this._onUpdate);
    }

    disconnectedCallback() {
      if (this._onUpdate) {
        document.removeEventListener(EVENT, this._onUpdate);
      }
    }

    productJsonUrl(handle) {
      const root = window.Shopify?.routes?.root || '/';
      const base = root.endsWith('/') ? root : `${root}/`;
      return `${base}products/${handle}.js`;
    }

    async fetchProduct(handle) {
      const res = await fetch(this.productJsonUrl(handle));
      if (!res.ok) throw new Error('not found');
      return res.json();
    }

    storedCardHtml(stored) {
      const title = stored.title || stored.handle;
      const img = stored.image || '';
      const price = stored.price || '';
      const url = stored.url || `/products/${stored.handle}`;
      const safeTitle = String(title).replace(/</g, '&lt;');

      return `
        <article class="z-wishlist__card">
          <button type="button" class="z-wishlist__remove" data-z-wishlist-remove="${stored.handle}" aria-label="Remove from wishlist">×</button>
          <a href="${url}" class="z-wishlist__card-link">
            ${
              img
                ? `<img src="${img}" alt="${safeTitle}" width="400" height="400" loading="lazy" class="z-wishlist__img">`
                : '<div class="z-wishlist__img z-wishlist__img--placeholder"></div>'
            }
            <div class="z-wishlist__card-body">
              <h2 class="z-wishlist__card-title">${safeTitle}</h2>
              ${price ? `<p class="z-wishlist__card-price">${price}</p>` : ''}
            </div>
          </a>
        </article>
      `;
    }

    cardHtml(product, stored) {
      const img = product.featured_image || stored.image || '';
      const priceCents =
        product.price ??
        product.variants?.[0]?.price ??
        0;
      const price = formatMoney(
        priceCents,
        window.Shopify?.currency?.active || 'USD'
      );
      const root = window.Shopify?.routes?.root || '/';
      const url = product.url
        ? `${root.replace(/\/$/, '')}${product.url}`
        : stored.url || `${root}products/${product.handle}`;

      return `
        <article class="z-wishlist__card">
          <button type="button" class="z-wishlist__remove" data-z-wishlist-remove="${product.handle}" aria-label="Remove from wishlist">×</button>
          <a href="${url}" class="z-wishlist__card-link">
            ${
              img
                ? `<img src="${img}" alt="${product.title.replace(/"/g, '&quot;')}" width="400" height="400" loading="lazy" class="z-wishlist__img">`
                : '<div class="z-wishlist__img z-wishlist__img--placeholder"></div>'
            }
            <div class="z-wishlist__card-body">
              <h2 class="z-wishlist__card-title">${product.title}</h2>
              <p class="z-wishlist__card-price">${price}</p>
            </div>
          </a>
        </article>
      `;
    }

    async render() {
      const items = ZetyWishlist.getItems();
      if (!items.length) {
        this.grid.innerHTML = '';
        if (this.empty) this.empty.hidden = false;
        return;
      }
      if (this.empty) this.empty.hidden = true;
      this.grid.innerHTML = '<p class="z-wishlist__loading">Loading…</p>';

      const cards = await Promise.all(
        items.map(async (stored) => {
          try {
            const product = await this.fetchProduct(stored.handle);
            return this.cardHtml(product, stored);
          } catch {
            return this.storedCardHtml(stored);
          }
        })
      );

      this.grid.innerHTML = cards.filter(Boolean).join('') || '';
      if (!this.grid.children.length && this.empty) this.empty.hidden = false;

      this.grid.querySelectorAll('[data-z-wishlist-remove]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          ZetyWishlist.remove(btn.dataset.zWishlistRemove);
        });
      });
    }
  }

  function init() {
    if (window.__zetyWishlistInitialized) return;
    window.__zetyWishlistInitialized = true;

    updateBadges(false);
    syncToggleButtons();
    bindToggles();
    document.addEventListener(EVENT, () => {
      updateBadges(true);
      syncToggleButtons();
    });
    if (!customElements.get('zety-wishlist-page')) {
      customElements.define('zety-wishlist-page', ZetyWishlistPage);
    }

    document.querySelectorAll('zety-wishlist-page').forEach((el) => {
      if (typeof el.render === 'function') el.render();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
